import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { History } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Spinner } from '@/components/ui/spinner';
import { Timeline, TimelineItem, TimelineMarker, TimelineContent } from '@/components/ui/timeline';
import EmptyState from '../common/EmptyState';
import CompareVersionsDialog from './version-history/CompareVersionsDialog';
import VersionCard from './version-history/VersionCard';
import { useVersionHistory } from './version-history/useVersionHistory';
import { useDocumentDownload } from '@hooks/useDocumentDownload';
import type { AnalysisEntry, DocumentVersion } from '@/types/documents';

interface VersionHistoryViewProps {
  documentId?: string;
  analysisHistory?: AnalysisEntry[];
}

const VersionHistoryView: React.FC<VersionHistoryViewProps> = ({
  documentId,
  analysisHistory = [],
}) => {
  const { t } = useTranslation();
  const { versions, loading, error } = useVersionHistory(documentId, analysisHistory);
  const download = useDocumentDownload();
  const [selectedVersion, setSelectedVersion] = useState<DocumentVersion | null>(null);
  const [compareDialogOpen, setCompareDialogOpen] = useState(false);
  const [compareVersions, setCompareVersions] = useState<{
    from: DocumentVersion | null;
    to: DocumentVersion | null;
  }>({ from: null, to: null });

  const handleDownload = (version: DocumentVersion) => {
    if (documentId) void download(documentId, version.fileName, version.versionNumber);
  };

  const handleCompare = () => {
    if (selectedVersion) {
      const currentVersion = versions.find((v) => v.isCurrentVersion);
      setCompareVersions({ from: selectedVersion, to: currentVersion || null });
      setCompareDialogOpen(true);
    }
    setSelectedVersion(null);
  };

  const handleCompareDialogChange = (open: boolean) => {
    setCompareDialogOpen(open);
    if (!open) setCompareVersions({ from: null, to: null });
  };

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription className="revy-cjk leading-[1.5]">{error}</AlertDescription>
      </Alert>
    );
  }

  if (versions.length === 0) {
    return <EmptyState variant="bare" icon={History} title={t('documents.noVersionHistory')} />;
  }

  return (
    <div>
      <Timeline>
        {versions.map((version, index) => (
          <TimelineItem key={version.id} isLast={index === versions.length - 1}>
            <TimelineMarker />
            <TimelineContent>
              <VersionCard
                version={version}
                onSelect={setSelectedVersion}
                onDownload={handleDownload}
                onCompare={handleCompare}
              />
            </TimelineContent>
          </TimelineItem>
        ))}
      </Timeline>

      <CompareVersionsDialog
        open={compareDialogOpen}
        from={compareVersions.from}
        to={compareVersions.to}
        onOpenChange={handleCompareDialogChange}
      />
    </div>
  );
};

export default VersionHistoryView;
